import {
  BackComponent,
  CheckBox,
  FormInput,
  FormLocation,
  PageActionsStyles,
  PageMainContent,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { FormProvider, useFormContext, useSnackbar } from '@/providers'
import { paths } from '@/routes/paths'
import { useCreateCompany } from '@/services'
import { Address } from '@/types/address'
import { useGoBack, useGoToPath, useToggable } from '@/utils'
import { Button } from '@surein/ui'
import { equals, omit } from 'ramda'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckboxWrapper, PageHeader } from './styles'
import { validationSchema } from './validationSchema'

interface FormValues {
  companyName: string
  locationName: string
  companyAddress: Address
  locationAddress: Address
}

const modifyData = (data: FormValues) => ({
  company: {
    name: data.companyName,
    address: { ...data.companyAddress },
  },
  location: {
    name: data.locationName,
    address: {
      ...data.locationAddress,
    },
  },
})

export const AddCompanyPage = () => {
  const { t } = useTranslation()
  const createCompanyMutation = useCreateCompany()
  const snackbar = useSnackbar()
  const goToDashboard = useGoToPath(paths.dashboard)

  const handleSubmit = (values: FormValues) => {
    createCompanyMutation
      .mutateAsync(modifyData(values))
      .then(() => {
        snackbar.success(t('snackbars.success.createCompany'))
        goToDashboard()
      })
      .catch(() => snackbar.error(t('snackbars.error.generalMsg')))
  }

  return (
    <>
      <PageMainContent size="sm">
        <BackComponent />
        <PageHeader>
          <Typography variant="h4">{t('add_new_company')}</Typography>
          <Typography color={ThemeColor.b50} variant="p2Body">
            {t('business_info')}
          </Typography>
        </PageHeader>
        <FormProvider
          onSubmit={handleSubmit}
          validationSchema={validationSchema(
            t('validations', { returnObjects: true }),
          )}
        >
          <Location isLoading={createCompanyMutation.isLoading} />
        </FormProvider>
      </PageMainContent>
    </>
  )
}

const Location = ({ isLoading }: { isLoading: boolean }) => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormContext<FormValues>()
  const [same, setSame] = useToggable()

  const withoutAddressId = omit(['addressId', 'googleLocation'])

  const checkIfSame = equals(
    withoutAddressId(values.companyAddress),
    withoutAddressId(values.locationAddress),
  )

  const sameAsAddress = () => {
    setFieldValue('locationAddress', {
      city: values.companyAddress.city,
      country: values.companyAddress.country,
      postalCode: values.companyAddress.postalCode,
      street: values.companyAddress.street,
      streetNr: values.companyAddress.streetNr,
    })
  }

  const handleSameSelect = (e: boolean) => {
    setSame(!e)
    if (!e) {
      sameAsAddress()
    } else {
      setFieldValue('locationAddress', {
        city: '',
        country: '',
        postalCode: '',
        street: '',
        streetNr: '',
      })
    }
  }

  const handleCancel = useGoBack()

  useEffect(() => {
    if (values.companyAddress?.street) {
      if (same && !checkIfSame) setSame(false)

      if (!same && checkIfSame) setSame(true)
    }
  }, [values.companyAddress, values.locationAddress])

  return (
    <>
      <FormInput
        label={t('companyPage.addCompanyFields.companyName.label')}
        name="companyName"
      />
      <FormLocation
        // @ts-expect-error - Ignoring type issue with FormLocation props
        label={t('companyPage.addCompanyFields.companyAddress.label')}
        name="companyAddress"
      />
      <FormInput
        label={t('companyPage.addCompanyFields.locationName.label')}
        name="locationName"
      />
      <CheckboxWrapper>
        <CheckBox
          checked={same}
          label={t('sameAsAddress')}
          onChange={handleSameSelect}
        />
      </CheckboxWrapper>
      <FormLocation
        // @ts-expect-error - Ignoring type issue with FormLocation props
        label={t('companyPage.addCompanyFields.locationAddress.label')}
        name="locationAddress"
      />
      <PageActionsStyles>
        <Button
          onClick={handleCancel}
          variant="muted"
          shape="square"
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          shape="square"
          loading={isLoading}
        >
          {t('common.save')}
        </Button>
      </PageActionsStyles>
    </>
  )
}
